const CODE_VERIFIER_KEY = 'tc:code_verifier'
const OAUTH_STATE_KEY = 'tc:oauth_state'
const RETURN_TO_KEY = 'tc:return_to'

function randomString(size = 64): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'
  const bytes = new Uint8Array(size)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('')
}

async function sha256Base64Url(value: string): Promise<string> {
  const data = new TextEncoder().encode(value)
  const digest = await crypto.subtle.digest('SHA-256', data)
  const bytes = Array.from(new Uint8Array(digest))
  const base64 = btoa(String.fromCharCode(...bytes))
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function readSessionStorage(key: string): string | null {
  if (typeof window === 'undefined') return null
  return window.sessionStorage.getItem(key)
}

function writeSessionStorage(key: string, value: string): void {
  if (typeof window === 'undefined') return
  window.sessionStorage.setItem(key, value)
}

function removeSessionStorage(key: string): void {
  if (typeof window === 'undefined') return
  window.sessionStorage.removeItem(key)
}

export type PkceRequest = {
  codeVerifier: string
  codeChallenge: string
  state: string
}

export async function createPkceRequest(returnTo: string): Promise<PkceRequest> {
  const codeVerifier = randomString(64)
  const codeChallenge = await sha256Base64Url(codeVerifier)
  const state = randomString(32)

  writeSessionStorage(CODE_VERIFIER_KEY, codeVerifier)
  writeSessionStorage(OAUTH_STATE_KEY, state)
  writeSessionStorage(RETURN_TO_KEY, returnTo)

  return { codeVerifier, codeChallenge, state }
}

export function consumeCodeVerifier(): string | null {
  const value = readSessionStorage(CODE_VERIFIER_KEY)
  removeSessionStorage(CODE_VERIFIER_KEY)
  return value
}

export function consumeState(): string | null {
  const value = readSessionStorage(OAUTH_STATE_KEY)
  removeSessionStorage(OAUTH_STATE_KEY)
  return value
}

export function consumeReturnTo(): string {
  const value = readSessionStorage(RETURN_TO_KEY)
  removeSessionStorage(RETURN_TO_KEY)
  if (!value) return '/'
  if (!value.startsWith('/')) return '/'
  return value
}

export function clearPkceState(): void {
  removeSessionStorage(CODE_VERIFIER_KEY)
  removeSessionStorage(OAUTH_STATE_KEY)
  removeSessionStorage(RETURN_TO_KEY)
}
